import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, DollarSign, Users, QrCode as QrCodeIcon, Activity, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../utils/api';
import { getAuthData } from '../utils/auth';
import Logo from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';

const AdminPanel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState({});
  const [stats, setStats] = useState(null);
  const [editingTier, setEditingTier] = useState(null);
  const [tierForms, setTierForms] = useState({});
  const { user } = getAuthData();

  const fetchData = useCallback(async () => {
    try {
      const [tiersRes, statsRes] = await Promise.all([
        api.get('/admin/tiers'),
        api.get('/admin/stats')
      ]);
      setTiers(tiersRes.data.tiers);
      setTierForms(tiersRes.data.tiers);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Error loading admin data');
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!user?.isAdmin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user?.isAdmin, navigate, fetchData]);

  const handleTierChange = (tierKey, field, value) => {
    setTierForms(prev => ({
      ...prev,
      [tierKey]: {
        ...prev[tierKey],
        [field]: value
      }
    }));
  };

  const handleFeatureChange = (tierKey, index, value) => {
    const updatedFeatures = [...tierForms[tierKey].features];
    updatedFeatures[index] = value;
    handleTierChange(tierKey, 'features', updatedFeatures);
  };

  const handleSaveTier = async (tierKey) => {
    try {
      const tierData = tierForms[tierKey];
      await api.put(`/admin/tiers/${tierKey}`, {
        name: tierData.name,
        price: parseFloat(tierData.price),
        qr_limit: parseInt(tierData.qr_limit),
        scans_per_month: parseInt(tierData.scans_per_month),
        bulk_generation: tierData.bulk_generation,
        custom_branding: tierData.custom_branding,
        analytics_export: tierData.analytics_export,
        features: tierData.features
      });
      toast.success(`${tierData.name} tier updated successfully!`);
      setEditingTier(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating tier');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading admin panel...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-primary-50 to-white">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/20 shadow-sm">
        <div className="container mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <Link to="/dashboard" className="flex items-center gap-2">
            <Logo className="h-8 w-8" />
            <span className="text-2xl font-heading font-bold text-primary">cyQRus Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/dashboard">
              <Button variant="ghost">Back to Dashboard</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="h-10 w-10 text-accent" />
          <h1 className="text-4xl font-heading font-bold text-primary" data-testid="admin-title">Admin Panel</h1>
        </div>

        {/* Platform Stats */}
        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="card p-6 space-y-2" data-testid="stat-users">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Total Users</p>
                <Users className="h-5 w-5 text-accent" />
              </div>
              <p className="text-3xl font-heading font-bold text-primary">{stats.totalUsers}</p>
            </div>
            <div className="card p-6 space-y-2" data-testid="stat-qr-codes">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Total QR Codes</p>
                <QrCodeIcon className="h-5 w-5 text-accent" />
              </div>
              <p className="text-3xl font-heading font-bold text-primary">{stats.totalQRCodes}</p>
            </div>
            <div className="card p-6 space-y-2" data-testid="stat-scans">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Total Scans</p>
                <Activity className="h-5 w-5 text-accent" />
              </div>
              <p className="text-3xl font-heading font-bold text-primary">{stats.totalScans.toLocaleString()}</p>
            </div>
            <div className="card p-6 space-y-2" data-testid="stat-revenue">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Users by Tier</p>
                <DollarSign className="h-5 w-5 text-accent" />
              </div>
              <div className="text-sm space-y-1">
                {Object.entries(stats.usersByTier).map(([tier, count]) => (
                  <div key={tier} className="flex justify-between">
                    <span className="text-muted-foreground capitalize">{tier}:</span>
                    <span className="font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Pricing Tiers Management */}
        <div className="space-y-6">
          <h2 className="text-2xl font-heading font-bold text-primary">Pricing Tiers Management</h2>
          
          <div className="grid lg:grid-cols-3 gap-6">
            {Object.entries(tierForms).sort((a, b) => a[1].order - b[1].order).map(([tierKey, tier]) => (
              <div key={tierKey} className="card p-6 space-y-4" data-testid={`admin-tier-${tierKey}`}>
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-heading font-bold text-primary capitalize">{tierKey}</h3>
                  {editingTier === tierKey ? (
                    <Button size="sm" onClick={() => handleSaveTier(tierKey)} data-testid={`save-tier-${tierKey}`}>
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setEditingTier(tierKey)} data-testid={`edit-tier-${tierKey}`}>
                      Edit
                    </Button>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <Label>Tier Name</Label>
                    <Input
                      value={tier.name}
                      onChange={(e) => handleTierChange(tierKey, 'name', e.target.value)}
                      disabled={editingTier !== tierKey}
                      data-testid={`tier-name-${tierKey}`}
                    />
                  </div>

                  <div>
                    <Label>Price (USD/month)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={tier.price}
                      onChange={(e) => handleTierChange(tierKey, 'price', e.target.value)}
                      disabled={editingTier !== tierKey}
                      data-testid={`tier-price-${tierKey}`}
                    />
                  </div>

                  <div>
                    <Label>QR Code Limit (-1 = unlimited)</Label>
                    <Input
                      type="number"
                      value={tier.qr_limit}
                      onChange={(e) => handleTierChange(tierKey, 'qr_limit', e.target.value)}
                      disabled={editingTier !== tierKey}
                      data-testid={`tier-qr-limit-${tierKey}`}
                    />
                  </div>

                  <div>
                    <Label>Scans/Month (-1 = unlimited)</Label>
                    <Input
                      type="number"
                      value={tier.scans_per_month}
                      onChange={(e) => handleTierChange(tierKey, 'scans_per_month', e.target.value)}
                      disabled={editingTier !== tierKey}
                      data-testid={`tier-scans-${tierKey}`}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Features (one per line)</Label>
                    {tier.features.map((feature, idx) => (
                      <Input
                        key={idx}
                        value={feature}
                        onChange={(e) => handleFeatureChange(tierKey, idx, e.target.value)}
                        disabled={editingTier !== tierKey}
                        data-testid={`tier-feature-${tierKey}-${idx}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;
